import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { OvertimePaymentTableService } from "services/overtime-payment-table";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { numberToWords, getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  fullName: "",
  salaryLevel: "",
  salaryCoefficient: "",
  positionAllowanceCoefficient: "",
  overtimeHoursWeekday: "",
  overtimeHoursWeekend: "",
  overtimeHoursHoliday: "",
  overtimeHoursNight: "",
  compensatoryLeaveHours: "",
  signature: "",
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const formatMonth2Digits = (value) => String(value || "").replace(/\D/g, "").slice(0, 2);
const normalizeMonth = (value) => {
  const raw = formatMonth2Digits(value);
  if (!raw) return "";
  return raw.padStart(2, "0");
};

const roundMoney = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmt = (n) => new Intl.NumberFormat("vi-VN").format(Number(n) || 0);

function calculateRow(row, ctx) {
  const totalCoefficient = num(row.salaryCoefficient) + num(row.positionAllowanceCoefficient);
  const monthlySalaryRaw = parseVndIntegerInput(ctx.minimumWage) * totalCoefficient;
  const dailySalaryRaw =
    num(ctx.workingDaysInMonth) > 0 ? monthlySalaryRaw / num(ctx.workingDaysInMonth) : 0;
  const hourlySalaryRaw = num(ctx.workingHoursPerDay) > 0 ? dailySalaryRaw / num(ctx.workingHoursPerDay) : 0;

  const overtimeAmountWeekday =
    num(row.overtimeHoursWeekday) * hourlySalaryRaw * num(ctx.overtimeRateWeekday);
  const overtimeAmountWeekend =
    num(row.overtimeHoursWeekend) * hourlySalaryRaw * num(ctx.overtimeRateWeekend);
  const overtimeAmountHoliday =
    num(row.overtimeHoursHoliday) * hourlySalaryRaw * num(ctx.overtimeRateHoliday);
  const overtimeAmountNight =
    num(row.overtimeHoursNight) * hourlySalaryRaw * num(ctx.overtimeRateNight);

  const roundedOvertimeAmountWeekday = roundMoney(overtimeAmountWeekday);
  const roundedOvertimeAmountWeekend = roundMoney(overtimeAmountWeekend);
  const roundedOvertimeAmountHoliday = roundMoney(overtimeAmountHoliday);
  const roundedOvertimeAmountNight = roundMoney(overtimeAmountNight);

  const totalOvertimeAmount =
    roundedOvertimeAmountWeekday +
    roundedOvertimeAmountWeekend +
    roundedOvertimeAmountHoliday +
    roundedOvertimeAmountNight;
  const compensatoryLeaveAmount = roundMoney(
    num(row.compensatoryLeaveHours) * hourlySalaryRaw * num(ctx.compensatoryRate)
  );
  const actualPayment = totalOvertimeAmount - compensatoryLeaveAmount;

  return {
    ...row,
    totalCoefficient,
    monthlySalary: roundMoney(monthlySalaryRaw),
    dailySalary: roundMoney(dailySalaryRaw),
    hourlySalary: roundMoney(hourlySalaryRaw),
    overtimeAmountWeekday: roundedOvertimeAmountWeekday,
    overtimeAmountWeekend: roundedOvertimeAmountWeekend,
    overtimeAmountHoliday: roundedOvertimeAmountHoliday,
    overtimeAmountNight: roundedOvertimeAmountNight,
    totalOvertimeAmount,
    compensatoryLeaveAmount,
    actualPayment,
  };
}

function OvertimePaymentTableDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const [submitting, setSubmitting] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    docNumber: "",
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
    minimumWage: "",
    workingDaysInMonth: "26",
    workingHoursPerDay: "8",
    overtimeRateWeekday: "1.5",
    overtimeRateWeekend: "2",
    overtimeRateHoliday: "3",
    overtimeRateNight: "1.3",
    compensatoryRate: "1",
  });
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const response = await OvertimePaymentTableService.getById(id);
        const apiData = response.data ? response.data : response;

        setHeader({
          unit: apiData.unit || "",
          department: apiData.department || "",
          docNumber: apiData.unitNumber || "",
          month: normalizeMonth(String(apiData.month || today.getMonth() + 1)),
          year: String(apiData.year || today.getFullYear()),
          minimumWage: formatVndIntegerInput(apiData.minimumWage || 0),
          workingDaysInMonth: String(apiData.workingDaysInMonth || 26),
          workingHoursPerDay: String(apiData.workingHoursPerDay || 8),
          overtimeRateWeekday: String(apiData.overtimeRateWeekday || 1.5),
          overtimeRateWeekend: String(apiData.overtimeRateWeekend || 2),
          overtimeRateHoliday: String(apiData.overtimeRateHoliday || 3),
          overtimeRateNight: String(apiData.overtimeRateNight || 1.3),
          compensatoryRate: String(apiData.compensatoryRate || 1),
        });

        const mappedRows = (apiData.employeeOvertimes || []).map((emp) => ({
          fullName: emp.name || "",
          salaryLevel: emp.salaryLevel || "",
          salaryCoefficient: String(emp.salaryCoefficient ?? ""),
          positionAllowanceCoefficient: String(emp.positionAllowanceCoefficient ?? ""),
          overtimeHoursWeekday: String(emp.overtimeHoursWeekday ?? ""),
          overtimeHoursWeekend: String(emp.overtimeHoursWeekend ?? ""),
          overtimeHoursHoliday: String(emp.overtimeHoursHoliday ?? ""),
          overtimeHoursNight: String(emp.overtimeHoursNight ?? ""),
          compensatoryLeaveHours: String(emp.compensatoryLeaveHours ?? ""),
          signature: emp.signature || "",
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng làm thêm giờ");
      }
    })();
  }, [id]);

  const calculatedRows = useMemo(() => rows.map((row) => calculateRow(row, header)), [rows, header]);

  const totals = useMemo(() => {
    return calculatedRows.reduce(
      (acc, row) => ({
        overtimeAmountWeekday: acc.overtimeAmountWeekday + num(row.overtimeAmountWeekday),
        overtimeHoursWeekend: acc.overtimeHoursWeekend + num(row.overtimeHoursWeekend),
        overtimeAmountWeekend: acc.overtimeAmountWeekend + num(row.overtimeAmountWeekend),
        overtimeHoursHoliday: acc.overtimeHoursHoliday + num(row.overtimeHoursHoliday),
        overtimeAmountHoliday: acc.overtimeAmountHoliday + num(row.overtimeAmountHoliday),
        overtimeHoursNight: acc.overtimeHoursNight + num(row.overtimeHoursNight),
        overtimeAmountNight: acc.overtimeAmountNight + num(row.overtimeAmountNight),
        totalOvertimeAmount: acc.totalOvertimeAmount + num(row.totalOvertimeAmount),
        compensatoryLeaveHours: acc.compensatoryLeaveHours + num(row.compensatoryLeaveHours),
        compensatoryLeaveAmount: acc.compensatoryLeaveAmount + num(row.compensatoryLeaveAmount),
        actualPayment: acc.actualPayment + num(row.actualPayment),
      }),
      {
        overtimeAmountWeekday: 0,
        overtimeHoursWeekend: 0,
        overtimeAmountWeekend: 0,
        overtimeHoursHoliday: 0,
        overtimeAmountHoliday: 0,
        overtimeHoursNight: 0,
        overtimeAmountNight: 0,
        totalOvertimeAmount: 0,
        compensatoryLeaveHours: 0,
        compensatoryLeaveAmount: 0,
        actualPayment: 0,
      }
    );
  }, [calculatedRows]);

  const setHeaderField = (field, value) =>
    setHeader((prev) => {
      if (field === "minimumWage") {
        return { ...prev, [field]: formatVndIntegerInput(value) };
      }
      if (field === "month") {
        return { ...prev, [field]: formatMonth2Digits(value) };
      }
      return { ...prev, [field]: value };
    });
  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const buildPayload = () => {
    const year = String(header.year || "").trim();
    const month = normalizeMonth(String(header.month || "").trim());
    const unitNumber = String(header.docNumber || "").trim();
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const minimumWage = parseVndIntegerInput(header.minimumWage);
    const workingDaysInMonth = num(header.workingDaysInMonth);
    const workingHoursPerDay = num(header.workingHoursPerDay);
    const overtimeRateWeekday = num(header.overtimeRateWeekday);
    const overtimeRateWeekend = num(header.overtimeRateWeekend);
    const overtimeRateHoliday = num(header.overtimeRateHoliday);
    const overtimeRateNight = num(header.overtimeRateNight);
    const compensatoryRate = num(header.compensatoryRate);

    if (!/^\d{4}$/.test(year)) {
      throw new Error("Năm phải gồm 4 chữ số (ví dụ: 2026)");
    }
    if (!/^\d{1,2}$/.test(month) || Number(month) < 1 || Number(month) > 12) {
      throw new Error("Tháng không hợp lệ (chỉ từ 1 đến 12)");
    }
    if (!unitNumber) {
      throw new Error("Số chứng từ (Số) không được để trống");
    }
    if (!unit) {
      throw new Error("Đơn vị không được để trống");
    }
    if (!department) {
      throw new Error("Bộ phận không được để trống");
    }
    if (minimumWage < 0) {
      throw new Error("Lương tối thiểu không được âm");
    }
    if (workingDaysInMonth < 1) {
      throw new Error("Ngày công/tháng phải lớn hơn hoặc bằng 1");
    }
    if (workingHoursPerDay < 1) {
      throw new Error("Giờ công/ngày phải lớn hơn hoặc bằng 1");
    }
    if (
      overtimeRateWeekday < 0 ||
      overtimeRateWeekend < 0 ||
      overtimeRateHoliday < 0 ||
      overtimeRateNight < 0 ||
      compensatoryRate < 0
    ) {
      throw new Error("Các hệ số làm thêm/ nghỉ bù không được âm");
    }

    const employees = calculatedRows
      .map((item) => ({
        name: item.fullName?.trim() || "",
        salaryLevel: item.salaryLevel?.trim() || "",
        salaryCoefficient: num(item.salaryCoefficient),
        positionAllowanceCoefficient: num(item.positionAllowanceCoefficient),
        overtimeHoursWeekday: num(item.overtimeHoursWeekday),
        overtimeHoursWeekend: num(item.overtimeHoursWeekend),
        overtimeHoursHoliday: num(item.overtimeHoursHoliday),
        overtimeHoursNight: num(item.overtimeHoursNight),
        compensatoryLeaveHours: num(item.compensatoryLeaveHours),
        signature: item.signature?.trim() || "",
      }))
      .filter((item) => item.name && item.salaryLevel);

    if (!employees.length) {
      throw new Error("Bảng làm thêm giờ phải có ít nhất 1 nhân viên");
    }

    employees.forEach((item, index) => {
      const rowNo = index + 1;
      if (!item.name) {
        throw new Error(`Dòng ${rowNo}: Họ và tên không được để trống`);
      }
      if (!item.salaryLevel) {
        throw new Error(`Dòng ${rowNo}: Ngạch bậc lương/chức vụ không được để trống`);
      }
      if (item.salaryCoefficient < 0) {
        throw new Error(`Dòng ${rowNo}: Hệ số lương không được âm`);
      }
      if (item.positionAllowanceCoefficient < 0) {
        throw new Error(`Dòng ${rowNo}: Hệ số phụ cấp chức vụ không được âm`);
      }
      if (
        item.overtimeHoursWeekday < 0 ||
        item.overtimeHoursWeekend < 0 ||
        item.overtimeHoursHoliday < 0 ||
        item.overtimeHoursNight < 0
      ) {
        throw new Error(`Dòng ${rowNo}: Số giờ làm thêm không được âm`);
      }
      if (item.compensatoryLeaveHours < 0) {
        throw new Error(`Dòng ${rowNo}: Số giờ nghỉ bù không được âm`);
      }
    });

    return {
      year,
      month,
      unitNumber,
      unit,
      department,
      minimumWage,
      workingDaysInMonth,
      workingHoursPerDay,
      overtimeRateWeekday,
      overtimeRateWeekend,
      overtimeRateHoliday,
      overtimeRateNight,
      compensatoryRate,
      totalActualPayment: num(totals.actualPayment),
      totalEmployment: employees.length,
      employees,
    };
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      const payload = buildPayload();
      if (id) {
        await OvertimePaymentTableService.update(id, payload);
        toast.success("Cập nhật bảng làm thêm giờ thành công!");
      } else {
        await OvertimePaymentTableService.create(payload);
        toast.success("Tạo bảng làm thêm giờ thành công!");
      }
      navigation("/templates/overtime-payment-table");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    const html = getPrintTemplate({
      headerInfo: header,
      rows: calculatedRows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const addRow = () => setRows((prev) => [...prev, createRow()]);
  const removeRow = (index) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "250px",
      fontSize: "14px",
      fontFamily: "inherit",
      fontWeight: "bold",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "100px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 2100, fontSize: 12 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={s.headerTop}>
                  <div style={{ width: "60%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                      Đơn vị:{" "}
                      <input disabled={containView} style={s.inputDotted} value={header.unit} onChange={(e)=>setHeaderField("unit",e.target.value)} />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Bộ phận:{" "}
                      <input disabled={containView} style={s.inputDotted} value={header.department} onChange={(e)=>setHeaderField("department",e.target.value)} />
                    </div>
                  </div>
                  <div style={{ textAlign: "center", width: "40%" }}>
                    <div style={{ fontWeight: "bold" }}>Mẫu số: 03 - LĐTL</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", margin: "20px 0" }}>
                  <h2 style={{ fontSize: "20px", fontWeight: "bold", margin: 0, textTransform: "uppercase" }}>BẢNG THANH TOÁN TIỀN LÀM THÊM GIỜ</h2>
                  <div style={{ fontStyle: "italic", marginTop: 5 }}>
                    Tháng <input disabled={containView} style={{ ...s.inputDottedSmall, width: "40px" }} value={header.month} onChange={(e)=>setHeaderField("month",e.target.value)} onBlur={()=>setHeaderField("month", normalizeMonth(header.month))} />
                    {" "}năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "70px" }} value={header.year} onChange={(e)=>setHeaderField("year",e.target.value)} />
                  </div>
                  <div style={{ textAlign: "right", marginTop: 10 }}>
                    Số: <input disabled={containView} style={s.inputDottedSmall} value={header.docNumber} onChange={(e)=>setHeaderField("docNumber",e.target.value)} />
                  </div>
                </div>

                <SoftBox display="grid" gridTemplateColumns="repeat(8,minmax(0,1fr))" gap={2} mb={2}>
                  <div><label>Lương tối thiểu</label><input disabled={containView} type="text" inputMode="numeric" placeholder="Nhập lương tối thiểu" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.minimumWage} onChange={(e)=>setHeaderField("minimumWage",e.target.value)} /></div>
                  <div><label>Ngày công/tháng</label><input disabled={containView} type="text" inputMode="numeric" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.workingDaysInMonth} onChange={(e)=>setHeaderField("workingDaysInMonth",e.target.value)} /></div>
                  <div><label>Giờ công/ngày</label><input disabled={containView} type="text" inputMode="numeric" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.workingHoursPerDay} onChange={(e)=>setHeaderField("workingHoursPerDay",e.target.value)} /></div>
                  <div><label>HS ngày thường</label><input disabled={containView} type="text" inputMode="decimal" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.overtimeRateWeekday} onChange={(e)=>setHeaderField("overtimeRateWeekday",e.target.value)} /></div>
                  <div><label>HS T7,CN</label><input disabled={containView} type="text" inputMode="decimal" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.overtimeRateWeekend} onChange={(e)=>setHeaderField("overtimeRateWeekend",e.target.value)} /></div>
                  <div><label>HS lễ,tết</label><input disabled={containView} type="text" inputMode="decimal" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.overtimeRateHoliday} onChange={(e)=>setHeaderField("overtimeRateHoliday",e.target.value)} /></div>
                  <div><label>HS ban đêm</label><input disabled={containView} type="text" inputMode="decimal" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.overtimeRateNight} onChange={(e)=>setHeaderField("overtimeRateNight",e.target.value)} /></div>
                  <div><label>HS nghỉ bù</label><input disabled={containView} type="text" inputMode="decimal" style={{ ...s.inputNumber, border: "1px solid #d0d0d0", borderRadius: 6, height: 36 }} value={header.compensatoryRate} onChange={(e)=>setHeaderField("compensatoryRate",e.target.value)} /></div>
                </SoftBox>

                <div style={{ overflowX: "auto", border: "1px solid #ccc" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={s.th}>STT</th>
                        <th rowSpan={2} style={s.th}>Họ và tên</th>
                        <th rowSpan={2} style={s.th}>Ngạch bậc lương hoặc cấp bậc chức vụ</th>
                        <th rowSpan={2} style={s.th}>Hệ số lương</th>
                        <th rowSpan={2} style={s.th}>Hệ số phụ cấp chức vụ</th>
                        <th rowSpan={2} style={s.th}>Cộng hệ số</th>
                        <th rowSpan={2} style={s.th}>Tiền lương tháng</th>
                        <th colSpan={2} style={s.th}>Mức lương</th>
                        <th colSpan={2} style={s.th}>Làm thêm ngày làm việc</th>
                        <th colSpan={2} style={s.th}>Làm thêm ngày thứ bảy, chủ nhật</th>
                        <th colSpan={2} style={s.th}>Làm thêm ngày lễ, tết</th>
                        <th colSpan={2} style={s.th}>Làm thêm buổi đêm</th>
                        <th rowSpan={2} style={s.th}>Tổng cộng tiền</th>
                        <th colSpan={2} style={s.th}>Số ngày nghỉ bù</th>
                        <th rowSpan={2} style={s.th}>Số tiền thực được thanh toán</th>
                        <th rowSpan={2} style={s.th}>Người nhận tiền ký tên (D)</th>
                        {!containView && (
                          <th rowSpan={2} style={s.th}>Thao tác</th>
                        )}
                      </tr>
                      <tr>
                        <th style={s.th}>Ngày</th>
                        <th style={s.th}>Giờ</th>
                        <th style={s.th}>Số giờ</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Số giờ</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Số giờ</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Số giờ</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Số giờ</th>
                        <th style={s.th}>Thành tiền</th>
                      </tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        <th style={s.th}>8</th>
                        <th style={s.th}>9</th>
                        <th style={s.th}>10</th>
                        <th style={s.th}>11</th>
                        <th style={s.th}>12</th>
                        <th style={s.th}>13</th>
                        <th style={s.th}>14</th>
                        <th style={s.th}>15</th>
                        <th style={s.th}>16</th>
                        <th style={s.th}>17</th>
                        <th style={s.th}>18</th>
                        <th style={s.th}>D</th>
                        {!containView && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {calculatedRows.map((row, index) => (
                        <tr key={`overtime-row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                          <td style={s.td}><input disabled={containView} style={s.inputText} value={row.fullName} onChange={(e)=>setRowField(index,"fullName",e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} style={{ ...s.inputText, textAlign: "center" }} value={row.salaryLevel} onChange={(e)=>setRowField(index,"salaryLevel",e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.salaryCoefficient} onChange={(e)=>setRowField(index,"salaryCoefficient",e.target.value)} /></td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.positionAllowanceCoefficient} onChange={(e)=>setRowField(index,"positionAllowanceCoefficient",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.totalCoefficient)}</td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.monthlySalary)}</td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.dailySalary)}</td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.hourlySalary)}</td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.overtimeHoursWeekday} onChange={(e)=>setRowField(index,"overtimeHoursWeekday",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.overtimeAmountWeekday)}</td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.overtimeHoursWeekend} onChange={(e)=>setRowField(index,"overtimeHoursWeekend",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.overtimeAmountWeekend)}</td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.overtimeHoursHoliday} onChange={(e)=>setRowField(index,"overtimeHoursHoliday",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.overtimeAmountHoliday)}</td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.overtimeHoursNight} onChange={(e)=>setRowField(index,"overtimeHoursNight",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.overtimeAmountNight)}</td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.totalOvertimeAmount)}</td>
                          <td style={s.td}><input disabled={containView} type="text" inputMode="decimal" style={s.inputNumber} value={row.compensatoryLeaveHours} onChange={(e)=>setRowField(index,"compensatoryLeaveHours",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.compensatoryLeaveAmount)}</td>
                          <td style={{ ...s.td, textAlign:"right", paddingRight:6 }}>{fmt(row.actualPayment)}</td>
                          <td style={s.td}><input disabled={containView} style={{ ...s.inputText, textAlign: "center" }} value={row.signature} onChange={(e)=>setRowField(index,"signature",e.target.value)} /></td>
                          <td style={{ ...s.td, textAlign: "center" }}>
                            {!containView && (
                              <button type="button" style={{ border: "none", background: "transparent", color: "red" }} onClick={() => removeRow(index)}>X</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={10} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeAmountWeekday)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeHoursWeekend)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeAmountWeekend)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeHoursHoliday)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeAmountHoliday)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeHoursNight)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.overtimeAmountNight)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.totalOvertimeAmount)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.compensatoryLeaveHours)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.compensatoryLeaveAmount)}</td>
                        <td style={{ ...s.td, textAlign:"right", paddingRight:6, fontWeight:700 }}>{fmt(totals.actualPayment)}</td>
                        <td style={s.td}></td>
                        {!containView && <td style={s.td}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={2} fontStyle="italic">Tổng số tiền (Viết bằng chữ): <strong>{numberToWords(totals.actualPayment)}</strong></SoftBox>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>Trở về</SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {!containView && <SoftButton variant="outlined" color="dark" onClick={addRow}>THÊM DÒNG</SoftButton>}
                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>In Bảng</SoftButton>
                    ) : (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={submitting}>{id ? "Lưu" : "Tạo"}</SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default OvertimePaymentTableDetail;
